import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import useDeadlineOverride from "@/hooks/useDeadlineOverride";
import useCourseSettings from "@/hooks/useCourseSettings";
import useCohortSettings from "@/hooks/useCohortSettings";
import useClassSchedules, { type ClassSchedule, daysToLabel } from "@/hooks/useClassSchedules";
import { verifyAdminPassword } from "@/lib/verifyAdminPassword";

type Step = "hidden" | "password" | "panel";

interface AdminPanelProps {
  children?: ReactNode;
}

export default function AdminPanel({ children }: AdminPanelProps) {
  const { isManuallyClosed, setManualClose } = useDeadlineOverride();
  const { settings, updateSettings } = useCourseSettings();
  const {
    cohorts,
    activeCohortId,
    loading: cohortLoading,
    createCohort,
    setActive,
    refetch: refetchCohorts,
  } = useCohortSettings();
  const [step, setStep] = useState<Step>("hidden");
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordChecking, setPasswordChecking] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [tab, setTab] = useState<"toggle" | "dates" | "classes">("toggle");

  // Cohort creation state
  const [showNewCohortInput, setShowNewCohortInput] = useState(false);
  const [newCohortName, setNewCohortName] = useState("");
  const [cohortCreating, setCohortCreating] = useState(false);
  const [cohortError, setCohortError] = useState("");

  // Class schedule management
  const {
    schedules,
    loading: classLoading,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  } = useClassSchedules(activeCohortId);

  // Local editing state for class schedules
  const [editingClasses, setEditingClasses] = useState<Record<number, Partial<ClassSchedule>>>();

  // Track which classes have unsaved changes
  const [dirtyClasses, setDirtyClasses] = useState<Set<number>>(new Set());

  // Track unsaved changes in "dates" tab too
  const [datesDirty, setDatesDirty] = useState(false);
  const [localDates, setLocalDates] = useState<typeof settings>(settings);

  // Close confirmation modal
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Save status feedback
  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Sync editing state with fetched schedules
  useEffect(() => {
    const map: Record<number, Partial<ClassSchedule>> = {};
    schedules.forEach((s) => {
      map[s.id] = { ...s };
    });
    setEditingClasses(map);
    setDirtyClasses(new Set());
  }, [schedules]);

  // Sync local dates when settings change or tab opens
  useEffect(() => {
    setLocalDates(settings);
    setDatesDirty(false);
  }, [settings, tab]);

  const WEEKDAYS = ["월", "화", "수", "목", "금", "토", "일"];

  const handleTriggerClick = () => {
    const next = clickCount + 1;
    setClickCount(next);

    if (clickTimer) clearTimeout(clickTimer);

    if (next >= 3) {
      setClickCount(0);
      setStep("password");
      setPasswordInput("");
      setPasswordError(false);
      return;
    }

    const timer = setTimeout(() => {
      setClickCount(0);
    }, 1200);
    setClickTimer(timer);
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordChecking(true);
    setPasswordError(false);
    try {
      const trimmed = passwordInput.trim();
      if (!trimmed) {
        setPasswordError(true);
        return;
      }
      const valid = await verifyAdminPassword(trimmed);
      if (!valid) {
        setPasswordError(true);
        setPasswordInput("");
      } else {
        setStep("panel");
        setPasswordInput("");
        setPasswordError(false);
      }
    } catch {
      setPasswordError(true);
      setPasswordInput("");
    } finally {
      setPasswordChecking(false);
    }
  };

  const handleClose = () => {
    const hasUnsavedClasses = dirtyClasses.size > 0;
    const hasUnsavedDates = datesDirty;
    if (hasUnsavedClasses || hasUnsavedDates) {
      setShowCloseConfirm(true);
      return;
    }
    doClose();
  };

  const doClose = () => {
    setShowCloseConfirm(false);
    setStep("hidden");
    setPasswordInput("");
    setPasswordError(false);
    setDirtyClasses(new Set());
    setDatesDirty(false);
    setSaveMessage(null);
  };

  const handleDiscardAndClose = () => {
    // Revert editing state to server data
    const map: Record<number, Partial<ClassSchedule>> = {};
    schedules.forEach((s) => {
      map[s.id] = { ...s };
    });
    setEditingClasses(map);
    setDirtyClasses(new Set());
    setLocalDates(settings);
    setDatesDirty(false);
    setSaveMessage(null);
    doClose();
  };

  const handleDateChange = (field: keyof typeof settings, value: string) => {
    setLocalDates((prev) => ({ ...prev, [field]: value }));
    setDatesDirty(true);
  };

  const handleCreateCohort = async () => {
    const trimmed = newCohortName.trim();
    if (!trimmed) {
      setCohortError("기수 이름을 입력해 주세요.");
      return;
    }
    setCohortCreating(true);
    setCohortError("");
    try {
      const created = await createCohort(trimmed);
      if (created) {
        setActive(created.id);
        setNewCohortName("");
        setShowNewCohortInput(false);
      }
    } catch {
      setCohortError("기수 생성에 실패했어요. 다시 시도해 주세요.");
    } finally {
      setCohortCreating(false);
    }
  };

  // Class schedule handlers
  const handleClassFieldChange = (scheduleId: number, field: keyof ClassSchedule, value: unknown) => {
    setEditingClasses((prev) => {
      if (!prev) return prev;
      return { ...prev, [scheduleId]: { ...prev[scheduleId], [field]: value } };
    });
    setDirtyClasses((prev) => new Set(prev).add(scheduleId));
  };

  const handleToggleDay = (scheduleId: number, dayIndex: number) => {
    setEditingClasses((prev) => {
      if (!prev) return prev;
      const current = prev[scheduleId];
      const currentDays: number[] = current?.days || [];
      const exists = currentDays.includes(dayIndex);
      const newDays = exists
        ? currentDays.filter((d) => d !== dayIndex)
        : [...currentDays, dayIndex];
      return { ...prev, [scheduleId]: { ...current, days: newDays } };
    });
    setDirtyClasses((prev) => new Set(prev).add(scheduleId));
  };

  const handleSaveClass = async (scheduleId: number) => {
    const edited = editingClasses?.[scheduleId];
    if (!edited) return;
    try {
      await updateSchedule(scheduleId, {
        name: edited.name,
        days: edited.days,
        start_time: edited.start_time,
        end_time: edited.end_time,
        start_date: edited.start_date,
        end_date: edited.end_date,
        sort_order: edited.sort_order,
      });
      setDirtyClasses((prev) => {
        const next = new Set(prev);
        next.delete(scheduleId);
        return next;
      });
      setSaveMessage({ type: "success", text: "저장되었습니다" });
      setTimeout(() => setSaveMessage(null), 2000);
    } catch {
      setSaveMessage({ type: "error", text: "저장에 실패했습니다. 다시 시도해 주세요." });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleSaveDates = async () => {
    try {
      await updateSettings(localDates);
      setDatesDirty(false);
      setSaveMessage({ type: "success", text: "저장되었습니다" });
      setTimeout(() => setSaveMessage(null), 2000);
    } catch {
      setSaveMessage({ type: "error", text: "저장에 실패했습니다. 다시 시도해 주세요." });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleAddClass = async () => {
    try {
      const newSortOrder = schedules.length;
      await createSchedule({
        cohort_id: activeCohortId!,
        name: "새 반",
        days: [],
        start_time: "20:00",
        end_time: "21:15",
        start_date: new Date().toISOString().split("T")[0],
        end_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        sort_order: newSortOrder,
      });
    } catch {
      // silently fail
    }
  };

  const handleDeleteClass = async (scheduleId: number) => {
    try {
      await deleteSchedule(scheduleId);
      setEditingClasses((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[scheduleId];
        return next;
      });
    } catch {
      // silently fail
    }
  };

  return (
    <>
      {/* 트리거 버튼: children 있으면 children, 없으면 기존 점(·) */}
      <button
        onClick={handleTriggerClick}
        className="inline cursor-default text-foreground-500 hover:text-foreground-700 transition-colors select-none"
        title=""
        aria-hidden="true"
        tabIndex={-1}
      >
        {children ?? "·"}
      </button>

      {/* 비밀번호 모달 */}
      {step === "password" && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-foreground-950/50 backdrop-blur-sm">
          <div className="bg-background-50 rounded-xl border border-background-200 p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-foreground-950">관리자 인증</h3>
              <button
                onClick={handleClose}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError(false);
                }}
                placeholder="비밀번호 입력"
                autoFocus
                className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900"
              />
              {passwordError && (
                <p className="text-xs text-primary-600">비밀번호가 일치하지 않습니다</p>
              )}
              <button
                type="submit"
                disabled={passwordChecking}
                className="w-full px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
              >
                {passwordChecking ? "확인 중..." : "확인"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 관리자 패널 */}
      {step === "panel" && (
        <div className="fixed bottom-6 right-6 z-[200] bg-background-50 rounded-xl border border-background-200 p-5 shadow-lg w-80 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold text-foreground-950">관리자 패널</span>
            <button
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer text-xs"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* 탭 */}
          <div className="flex rounded-full bg-background-100 p-0.5 mb-4">
            <button
              onClick={() => setTab("toggle")}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                tab === "toggle" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              신청 제어
            </button>
            <button
              onClick={() => setTab("dates")}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                tab === "dates" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              일정 설정
            </button>
            <button
              onClick={() => setTab("classes")}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                tab === "classes" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              반 관리
            </button>
          </div>

          {tab === "toggle" && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-700">수강 신청 마감</span>
                <button
                  onClick={() => setManualClose(!isManuallyClosed)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    isManuallyClosed ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${
                      isManuallyClosed ? "translate-x-5" : "translate-x-0"
                    }`}
                  ></span>
                </button>
              </div>
              <p className="mt-2 text-xs text-foreground-500">
                {isManuallyClosed ? "현재 마감 상태입니다" : "현재 신청 가능 상태입니다"}
              </p>
            </>
          )}

          {tab === "dates" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  신청 마감일
                </label>
                <input
                  type="date"
                  value={localDates.deadline.includes("T") ? localDates.deadline.split("T")[0] : localDates.deadline}
                  onChange={(e) => handleDateChange("deadline", `${e.target.value}T23:59`)}
                  className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>
              {datesDirty && (
                <button
                  onClick={handleSaveDates}
                  className="w-full py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  마감일 저장
                </button>
              )}
              <p className="text-[10px] text-foreground-500 leading-relaxed">
                마감일 이후에는 신청 폼이 자동으로 닫힙니다. 반별 개강일/종강일은 &quot;반 관리&quot; 탭에서 설정하세요.
              </p>
            </div>
          )}

          {tab === "classes" && (
            <div className="space-y-4">
              {/* 기수 선택 */}
              <div className="pb-3 border-b border-background-200">
                <label className="block text-xs font-semibold text-foreground-700 mb-2">
                  현재 기수
                </label>
                {cohortLoading ? (
                  <p className="text-xs text-foreground-500">불러오는 중...</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {cohorts.map((c) => {
                      const isActive = c.id === activeCohortId;
                      return (
                        <button
                          key={c.id}
                          onClick={() => setActive(c.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                            isActive
                              ? "bg-primary-500 border-primary-500 text-background-50"
                              : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                    {!showNewCohortInput && (
                      <button
                        onClick={() => setShowNewCohortInput(true)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-background-400 text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-add-line mr-1"></i>새 기수
                      </button>
                    )}
                  </div>
                )}

                {showNewCohortInput && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={newCohortName}
                      onChange={(e) => {
                        setNewCohortName(e.target.value);
                        setCohortError("");
                      }}
                      placeholder="예: 2기"
                      autoFocus
                      className="flex-1 px-3 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateCohort();
                        if (e.key === "Escape") {
                          setShowNewCohortInput(false);
                          setNewCohortName("");
                          setCohortError("");
                        }
                      }}
                    />
                    <button
                      onClick={handleCreateCohort}
                      disabled={cohortCreating}
                      className="px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                    >
                      {cohortCreating ? "생성 중..." : "생성"}
                    </button>
                    <button
                      onClick={() => {
                        setShowNewCohortInput(false);
                        setNewCohortName("");
                        setCohortError("");
                      }}
                      className="px-2 py-1.5 rounded-md text-foreground-600 hover:text-foreground-800 text-xs cursor-pointer whitespace-nowrap"
                    >
                      취소
                    </button>
                  </div>
                )}
                {cohortError && (
                  <p className="mt-1.5 text-[11px] text-primary-600">{cohortError}</p>
                )}
              </div>

              {/* 반 목록 */}
              {classLoading ? (
                <p className="text-xs text-foreground-500 text-center py-4">불러오는 중...</p>
              ) : schedules.length === 0 ? (
                <p className="text-xs text-foreground-500 text-center py-4">
                  등록된 반이 없습니다. 아래 버튼으로 추가해 주세요.
                </p>
              ) : (
                schedules.map((s) => {
                  const edited = editingClasses?.[s.id];
                  const dayLabel = daysToLabel(edited?.days || s.days);
                  const preview = edited
                    ? `${edited.name || s.name} ${edited.start_time || s.start_time} (${(edited.start_date || s.start_date).slice(5).replace("-", "/")} 개강)`
                    : "";
                  return (
                    <div
                      key={s.id}
                      className="p-3 rounded-lg bg-background-100 border border-background-200 space-y-2.5"
                    >
                      {/* 이름 */}
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={edited?.name ?? s.name}
                          onChange={(e) => handleClassFieldChange(s.id, "name", e.target.value)}
                          className="flex-1 px-2.5 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs font-semibold text-foreground-900 outline-none focus:border-primary-400"
                        />
                        <button
                          onClick={() => handleDeleteClass(s.id)}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 hover:text-primary-600 text-foreground-600 text-xs cursor-pointer transition-colors shrink-0"
                          title="반 삭제"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>

                      {/* 요일 */}
                      <div>
                        <label className="block text-[10px] text-foreground-600 mb-1">
                          요일 {dayLabel ? `(${dayLabel})` : ""}
                        </label>
                        <div className="flex gap-1">
                          {WEEKDAYS.map((day, di) => {
                            const currentDays = edited?.days ?? s.days;
                            const active = currentDays.includes(di);
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => handleToggleDay(s.id, di)}
                                className={`w-7 h-7 rounded-full text-[10px] font-semibold border transition-colors cursor-pointer ${
                                  active
                                    ? "bg-primary-500 border-primary-500 text-background-50"
                                    : "bg-background-50 border-background-300 text-foreground-600 hover:border-primary-300"
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* 시간 */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-foreground-600 mb-1">시작</label>
                          <input
                            type="time"
                            value={edited?.start_time ?? s.start_time}
                            onChange={(e) => handleClassFieldChange(s.id, "start_time", e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-foreground-600 mb-1">종료</label>
                          <input
                            type="time"
                            value={edited?.end_time ?? s.end_time}
                            onChange={(e) => handleClassFieldChange(s.id, "end_time", e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                          />
                        </div>
                      </div>

                      {/* 날짜 */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-foreground-600 mb-1">개강일</label>
                          <input
                            type="date"
                            value={edited?.start_date ?? s.start_date}
                            onChange={(e) => handleClassFieldChange(s.id, "start_date", e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-foreground-600 mb-1">종강일</label>
                          <input
                            type="date"
                            value={edited?.end_date ?? s.end_date}
                            onChange={(e) => handleClassFieldChange(s.id, "end_date", e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-900 outline-none focus:border-primary-400"
                          />
                        </div>
                      </div>

                      {/* 폼 미리보기 */}
                      {preview && (
                        <p className="text-[10px] text-foreground-500 bg-background-50 rounded-md px-2 py-1">
                          신청 폼: {preview}
                        </p>
                      )}

                      {/* 저장 버튼 */}
                      {dirtyClasses.has(s.id) && (
                        <button
                          onClick={() => handleSaveClass(s.id)}
                          className="w-full py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
                        >
                          변경사항 저장
                        </button>
                      )}
                    </div>
                  );
                })
              )}

              {/* 반 추가 버튼 */}
              {activeCohortId && (
                <button
                  onClick={handleAddClass}
                  className="flex items-center justify-center gap-1.5 w-full py-2 rounded-md border border-dashed border-background-400 text-xs font-medium text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  반 추가
                </button>
              )}

              <p className="text-[10px] text-foreground-500 leading-relaxed">
                반 이름, 요일, 시간, 날짜를 설정하면 신청 폼의 선택지와 수업 안내에 자동으로 반영됩니다.
              </p>
            </div>
          )}

          {/* 대시보드 바로가기 */}
          <div className="mt-4 pt-3 border-t border-background-200">
            <a
              href="/admin"
              className="flex items-center justify-center gap-1.5 w-full py-2 rounded-md bg-background-100 hover:bg-background-200 text-xs font-semibold text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-dashboard-line"></i>
              신청자 대시보드 열기
            </a>
          </div>

          {/* 저장 피드백 토스트 */}
          {saveMessage && (
            <div
              className={`mt-3 px-3 py-2 rounded-md text-xs font-medium text-center transition-all ${
                saveMessage.type === "success"
                  ? "bg-accent-100 text-accent-900"
                  : "bg-primary-100 text-primary-700"
              }`}
            >
              {saveMessage.text}
            </div>
          )}
        </div>
      )}

      {/* 닫기 확인 모달 */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-foreground-950/50 backdrop-blur-sm">
          <div className="bg-background-50 rounded-xl border border-background-200 p-6 w-full max-w-xs">
            <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
              저장하지 않은 변경사항이 있습니다
            </h3>
            <p className="text-xs text-foreground-600 mb-5 leading-relaxed">
              변경사항을 저장하지 않고 닫으면 모든 수정 내용이 사라집니다.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDiscardAndClose}
                className="flex-1 px-4 py-2 rounded-md border border-background-300 text-xs font-semibold text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap transition-colors"
              >
                저장 안 함
              </button>
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="flex-1 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                계속 편집
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}